import { AlertCircle } from 'lucide-react';
import { formatPrice, useTranslation } from '@dukanti/shared';
import type {
  FulfillmentQuote,
  FulfillmentSelection,
  Locality,
  TranslationKey,
} from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import type {
  PreparedOrderDeliveryDraft,
  PreparedOrderLine,
} from '../lib/admin-prepared-order-schema';
import {
  getPreparedOrderBlockingErrors,
  normalizePreparedOrderPhone,
} from '../lib/admin-prepared-order-schema';

export interface AdminPreparedOrderReviewProps {
  readonly lines: readonly PreparedOrderLine[];
  readonly delivery: PreparedOrderDeliveryDraft;
  readonly localities: readonly Locality[];
  readonly isPending: boolean;
  readonly errorKey: TranslationKey | null;
  readonly fulfillment: FulfillmentSelection | null;
  readonly quote: FulfillmentQuote | null;
  readonly onRefresh: () => void;
  readonly onEditItems: () => void;
  readonly onEditDelivery: () => void;
  readonly onCreate: () => void;
}

function localityDisplay(locality: Locality | undefined, lang: 'ar' | 'he' | 'en'): string {
  if (!locality) return '';
  if (lang === 'ar') return locality.name_ar ?? locality.name_he ?? locality.name_en ?? '';
  if (lang === 'he') return locality.name_he ?? locality.name_ar ?? locality.name_en ?? '';
  return locality.name_en ?? locality.name_ar ?? locality.name_he ?? '';
}

export function AdminPreparedOrderReview(props: AdminPreparedOrderReviewProps): React.JSX.Element {
  const { t, lang } = useTranslation();
  const subtotal = props.quote?.subtotalCents;
  const locality = props.localities.find((candidate) => candidate.id === props.delivery.localityId);
  const phone = normalizePreparedOrderPhone(props.delivery);
  const displayPhone = phone.ok ? phone.e164 : props.delivery.phoneNumber;
  const contactSummary = [props.delivery.customerName.trim(), displayPhone.trim()]
    .filter(Boolean)
    .join(' · ');
  const addressSummary = [localityDisplay(locality, lang), props.delivery.addressLine1.trim()]
    .filter(Boolean)
    .join(' · ');
  const blockingErrors = getPreparedOrderBlockingErrors({
    lines: props.lines,
    delivery: props.delivery,
    localities: props.localities,
    fulfillment: props.fulfillment ?? undefined,
  });
  const isCreateDisabled = props.isPending || blockingErrors.length > 0 || !props.quote;

  return (
    <section className="border-border bg-card shadow-admin-card rounded-lg border p-4">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-display text-foreground text-xl font-semibold">
          {t('admin.orders.prepare.review.title' as TranslationKey)}
        </h2>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={props.onEditItems}>
            {t('admin.orders.prepare.review.editItems' as TranslationKey)}
          </Button>
          <Button type="button" variant="outline" onClick={props.onEditDelivery}>
            {t('admin.orders.prepare.review.editDelivery' as TranslationKey)}
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {props.lines.map((line) => (
          <div
            key={line.variant.variant_id}
            className="border-border flex items-center justify-between gap-3 border-b pb-2 text-sm"
          >
            <span className="min-w-0 break-words text-start">
              {line.variant.product_name} · {line.variant.option_summary} × {line.quantity}
            </span>
          </div>
        ))}
        <div className="text-foreground flex items-center justify-between font-semibold">
          <span>{t('admin.orders.prepare.lines.subtotal' as TranslationKey)}</span>
          <span>
            {subtotal === undefined ? t('fulfillment.quoteLoading') : formatPrice(subtotal, lang)}
          </span>
        </div>
        {props.quote ? (
          <>
            <div className="flex justify-between gap-3 text-sm">
              <span>
                {t(
                  props.quote.selection.method === 'pickup'
                    ? 'fulfillment.freePickup'
                    : 'fulfillment.deliveryFee',
                )}
              </span>
              <span>{formatPrice(props.quote.feeCents, lang)}</span>
            </div>
            <div className="flex justify-between gap-3 font-semibold">
              <span>{t('checkout.total')}</span>
              <span>{formatPrice(props.quote.totalCents, lang)}</span>
            </div>
          </>
        ) : null}
      </div>

      <div className="bg-muted mt-5 rounded-lg p-4 text-start text-sm">
        <p className="text-foreground font-medium">{t('fulfillment.title')}</p>
        {contactSummary ? <p className="text-muted-foreground mt-2">{contactSummary}</p> : null}
        {props.fulfillment?.method === 'delivery' ? (
          <>
            <p>{t('fulfillment.delivery')}</p>
            {addressSummary ? <p className="text-muted-foreground">{addressSummary}</p> : null}
          </>
        ) : (
          <>
            <p>{t('fulfillment.pickup')}</p>
            {props.quote?.pickupPlace ? (
              <>
                <bdi className="block break-words">{props.quote.pickupPlace.name}</bdi>
                <bdi className="block whitespace-pre-wrap break-words">
                  {props.quote.pickupPlace.details}
                </bdi>
              </>
            ) : null}
            <p className="text-muted-foreground mt-2">{t('fulfillment.pickupStockNotice')}</p>
          </>
        )}
      </div>

      <p className="text-muted-foreground mt-4 text-sm">
        {t('admin.orders.prepare.review.noPaymentNotice' as TranslationKey)}
      </p>

      {blockingErrors.length > 0 ? (
        <div
          className="border-border bg-background mt-4 rounded-lg border p-3 text-start text-sm"
          role="status"
          aria-live="polite"
        >
          <p className="text-foreground font-medium">
            {t('admin.orders.prepare.review.requirementsTitle' as TranslationKey)}
          </p>
          <ul className="text-muted-foreground mt-2 list-disc space-y-1 ps-5">
            {blockingErrors.map((error) => (
              <li key={error}>{t(error as TranslationKey)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {props.errorKey ? (
        <p className="text-destructive mt-4 flex items-center gap-2 text-sm" role="alert">
          <AlertCircle className="h-4 w-4" />
          {t(props.errorKey as TranslationKey)}
        </p>
      ) : null}

      {!props.quote ? (
        <div role="status" className="mt-4 space-y-2">
          <p>{t('fulfillment.optionsChanged')}</p>
          <Button
            type="button"
            variant="outline"
            disabled={props.isPending}
            onClick={props.onRefresh}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}
      <div className="mt-5 flex justify-end">
        <Button type="button" onClick={props.onCreate} disabled={isCreateDisabled}>
          {props.isPending
            ? t('admin.orders.prepare.review.creating' as TranslationKey)
            : t('admin.orders.prepare.review.create' as TranslationKey)}
        </Button>
      </div>
    </section>
  );
}
